import { measureUnit } from '@obraplan/core/domain/takeoff/applies-to';
import {
  resolveItemQuantity,
  type TakeoffQuantities,
} from '@obraplan/core/domain/takeoff/consolidation';
import type {
  CatalogDiscipline,
  CatalogService,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { metros } from './formato';
import { medidosDaTipologia, totaisDaTipologia } from './medido-da-tipologia';
import {
  comodosDaTipologia,
  fundacaoDaTipologia,
  pileDaTipologia,
  quantitiesOf,
} from './resumo-dos-itens';
import {
  aprovacaoDe,
  nomeDoAprovado,
  type AprovacaoDoServico,
  type ServicosAprovados,
} from './servicos-aprovados';
import type { TypologyDraft, WorkspaceDraft } from './typology-draft';

/**
 * O desenho travado na bancada (Fase 33, etapa 3, RN-439).
 *
 * O servidor não refaz a quantidade: quem a conta é a bancada, a partir do desenho. Por isso é
 * ela que segura o serviço aprovado — a gravação do desenho que muda a quantidade dele não sobe.
 *
 * A conta compara a mesma bancada **antes e depois** da mudança, e não contra a foto da
 * aprovação. Comparar contra a foto travaria o desenho para sempre na primeira mudança da
 * Estrutura (um apto a mais, que a D6 deixa livre): a quantidade da obra nunca mais bateria.
 * Pela mesma razão a conta é **por tipologia**, uma vez: quantos aptos repetem a tipologia é da
 * Estrutura, e não entra aqui.
 *
 * Compara o medido do serviço e a quantidade de cada item dele, porque a foto guarda os itens:
 * o aço da estaca reparte o kg pelo tipo dela, e a aba de referência troca a quantidade do item
 * que lê da planilha sem mexer no medido.
 */

/** Uma quantidade de serviço aprovado que a mudança mexeria, numa tipologia. */
export interface MudancaNoAprovado {
  /** "ALVENARIA › ALVENARIA DE BLOCO" — o nome da foto, como a recusa do servidor o diz. */
  readonly servico: string;
  readonly tipologia: string;
  /** O item que muda; ausente quando é o medido do próprio serviço. */
  readonly item?: string;
  /** `undefined` é "sem quantidade". */
  readonly antes: number | undefined;
  readonly depois: number | undefined;
  readonly unidade?: string;
}

interface Aprovado {
  readonly disciplina: CatalogDiscipline;
  readonly servico: CatalogService;
  readonly aprovacao: AprovacaoDoServico;
}

/** Os serviços do catálogo que estão aprovados. */
function aprovadosDoCatalogo(
  catalog: ServiceCatalog | undefined,
  aprovados: ServicosAprovados,
): readonly Aprovado[] {
  if (catalog === undefined) return [];
  return catalog.disciplines.flatMap((disciplina) =>
    disciplina.services.flatMap((servico) => {
      const aprovacao = aprovacaoDe(aprovados, servico.key);
      return aprovacao === undefined ? [] : [{ disciplina, servico, aprovacao }];
    }),
  );
}

/**
 * A mesma quantidade? O desenho refaz a conta em ponto flutuante: a sobra do décimo quinto
 * dígito não é mudança, e a travaria sem nada para desfazer.
 */
function mesma(a: number | undefined, b: number | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
}

/** O que a consolidação lê desta tipologia — a mesma conta da aba de itens. */
interface ContaDaTipologia {
  readonly medidos: Readonly<Record<string, number>>;
  readonly quantities: TakeoffQuantities;
}

function contaDa(workspace: WorkspaceDraft, typology: TypologyDraft): ContaDaTipologia {
  const catalog = workspace.catalog!;
  // A taxa de aço por peça muda o medido do aço da fundação (Fase 41, RN-545, RN-547).
  const fundacao = fundacaoDaTipologia(workspace, typology);
  const medidos = medidosDaTipologia(
    catalog,
    totaisDaTipologia(workspace, typology),
    typology,
    fundacao,
  );
  return {
    medidos,
    quantities: quantitiesOf(
      typology,
      medidos,
      catalog,
      pileDaTipologia(typology, workspace.pileTypes),
      comodosDaTipologia(workspace, typology),
      fundacao,
    ),
  };
}

/** A primeira diferença do serviço nesta tipologia: o medido, ou senão o primeiro item. */
function diferencaDoServico(
  { servico, aprovacao }: Aprovado,
  tipologia: string,
  antes: ContaDaTipologia,
  depois: ContaDaTipologia,
): MudancaNoAprovado | undefined {
  const nome = nomeDoAprovado(aprovacao);
  const medidoAntes = antes.medidos[servico.key];
  const medidoDepois = depois.medidos[servico.key];
  if (!mesma(medidoAntes, medidoDepois)) {
    return {
      servico: nome,
      tipologia,
      antes: medidoAntes,
      depois: medidoDepois,
      // A do cadastro; sem ela, a da medida, que é a do número que o desenho dá.
      unidade: servico.unit ?? measureUnit(servico.appliesTo, servico.measureBy),
    };
  }
  for (const item of servico.items) {
    const deAntes = resolveItemQuantity(item, servico.key, antes.quantities).quantity;
    const deDepois = resolveItemQuantity(item, servico.key, depois.quantities).quantity;
    if (mesma(deAntes, deDepois)) continue;
    return {
      servico: nome,
      tipologia,
      item: item.description,
      antes: deAntes,
      depois: deDepois,
      ...(item.unit === undefined ? {} : { unidade: item.unit }),
    };
  }
  return undefined;
}

/**
 * O que a mudança de `antes` para `depois` faz com os serviços aprovados — vazio quando nada.
 *
 * Só as tipologias que estão nas duas: a que entrou ou saiu é da Estrutura. `tipologias`
 * restringe a conta às que a mudança alcança (a aba de referência é de uma tipologia só).
 */
export function mudancasNosAprovados(
  antes: WorkspaceDraft,
  depois: WorkspaceDraft,
  aprovados: ServicosAprovados | undefined,
  tipologias?: ReadonlySet<string>,
): readonly MudancaNoAprovado[] {
  if (aprovados === undefined || aprovados.size === 0) return [];
  if (antes.catalog === undefined || depois.catalog === undefined) return [];
  const doCatalogo = aprovadosDoCatalogo(depois.catalog, aprovados);
  if (doCatalogo.length === 0) return [];

  const deAntes = new Map(antes.typologies.map((typology) => [typology.unitTypeId, typology]));
  const mudancas: MudancaNoAprovado[] = [];
  for (const typology of depois.typologies) {
    if (tipologias !== undefined && !tipologias.has(String(typology.unitTypeId))) continue;
    const anterior = deAntes.get(typology.unitTypeId);
    if (anterior === undefined) continue;
    const contaAntes = contaDa(antes, anterior);
    const contaDepois = contaDa(depois, typology);
    for (const aprovado of doCatalogo) {
      const diferenca = diferencaDoServico(aprovado, typology.name, contaAntes, contaDepois);
      if (diferenca !== undefined) mudancas.push(diferenca);
    }
  }
  return mudancas;
}

function quantidade(valor: number | undefined, unidade: string | undefined): string {
  if (valor === undefined) return 'sem quantidade';
  return unidade === undefined ? metros(valor) : `${metros(valor)} ${unidade}`;
}

/** "ALVENARIA › ALVENARIA DE BLOCO, Tipo A: 39,20 → 41,00 m²" — o item, quando é ele. */
export function textoDaMudanca(mudanca: MudancaNoAprovado): string {
  const onde =
    mudanca.item === undefined
      ? `${mudanca.servico}, ${mudanca.tipologia}`
      : `${mudanca.servico}, ${mudanca.tipologia}, ${mudanca.item}`;
  const de = quantidade(mudanca.antes, mudanca.unidade);
  const para = quantidade(mudanca.depois, mudanca.unidade);
  return `${onde}: ${de} → ${para}`;
}

/** As primeiras, e quantas mais — o aviso não vira uma lista sem fim. */
export function primeirasMudancas(
  mudancas: readonly MudancaNoAprovado[],
  quantas = 3,
): { readonly textos: readonly string[]; readonly mais: number } {
  return {
    textos: mudancas.slice(0, quantas).map(textoDaMudanca),
    mais: Math.max(0, mudancas.length - quantas),
  };
}

/**
 * A recusa da troca da aba de referência (decisão do Gilvan, 19/10/2026): é um clique de
 * alguém, e o "não" vem na hora, com o serviço e o antes × depois.
 */
export function recusaDaAba(sheetName: string, mudancas: readonly MudancaNoAprovado[]): string {
  const { textos, mais } = primeirasMudancas(mudancas);
  const oQue =
    sheetName === ''
      ? 'A tipologia não foi desligada da aba de referência'
      : `A aba “${sheetName}” não foi escolhida`;
  const resto = mais === 0 ? '' : ` (e mais ${String(mais)})`;
  return `${oQue}: ela muda a quantidade de serviço aprovado no orçamento, que não muda mais — ${textos.join('; ')}${resto}.`;
}
